import * as React from 'react';
import { matchPath } from 'react-router-dom';

import { ROUTES } from '@/configs/routes';
import type { TopLevelSectionSlug } from '@/entities/OrganizationSection';
import type { OrganizationSectionsStore } from '@/stores/local/shared/OrganizationSectionsStore';
import { useDevices } from '@/utils/devices';

import type { DetailPageKind } from '../layouts/OrganizationLayout/types';
import { getDetailPageKind } from '../layouts/OrganizationLayout/utils';

type UseOrganizationLayoutViewStateResult = {
  detailKind: DetailPageKind | null;
  enabledTopLevelSections: TopLevelSectionSlug[];
  hasEnabledTopLevelSections: boolean;
  hasSidebarSections: boolean;
  isSectionsConfigLoading: boolean;
  isSectionsConfigError: boolean;
  showNoSectionsStub: boolean;
  showMobileBottomNav: boolean;
};

export const useOrganizationLayoutViewState = ({
  pathname,
  organizationSectionsStore,
}: {
  pathname: string;
  organizationSectionsStore: OrganizationSectionsStore;
}): UseOrganizationLayoutViewStateResult => {
  const { isCompactViewport } = useDevices();

  const detailKind = React.useMemo(() => getDetailPageKind(pathname), [pathname]);
  const enabledTopLevelSections = organizationSectionsStore.enabledTopLevelSections;

  const hasEnabledTopLevelSections = enabledTopLevelSections.length > 0;
  const hasSidebarSections = enabledTopLevelSections.some((sectionSlug) => sectionSlug !== 'chat');

  const isSectionsConfigLoading =
    organizationSectionsStore.meta.isLoading || organizationSectionsStore.meta.isInitialMetaState;
  const isSectionsConfigError = organizationSectionsStore.meta.isError;

  const showNoSectionsStub =
    !isSectionsConfigError &&
    organizationSectionsStore.meta.isLoaded &&
    organizationSectionsStore.hasExplicitConfigurationForActiveOrganization &&
    !hasEnabledTopLevelSections;

  const documentsChatMatch = React.useMemo(
    () => matchPath(ROUTES.organization.documents.chat.path, pathname),
    [pathname],
  );
  const isDocumentsChatRoot = React.useMemo(() => {
    if (!documentsChatMatch) {
      return false;
    }

    return !documentsChatMatch.params?.documentId;
  }, [documentsChatMatch]);
  const isDocumentRoute = React.useMemo(() => {
    const match = matchPath({ path: ROUTES.organization.documents.document.path, end: false }, pathname);

    if (!match) {
      return false;
    }

    if (isDocumentsChatRoot) {
      return false;
    }

    return true;
  }, [pathname, isDocumentsChatRoot]);

  const hideBottomNavForUpsert =
    detailKind === 'noteCreate' ||
    detailKind === 'noteEdit' ||
    detailKind === 'insightCreate' ||
    detailKind === 'insightEdit' ||
    detailKind === 'followUpCreate' ||
    detailKind === 'followUpEdit';

  const showMobileBottomNav = isCompactViewport && !isDocumentRoute && !hideBottomNavForUpsert;

  return {
    detailKind,
    enabledTopLevelSections,
    hasEnabledTopLevelSections,
    hasSidebarSections,
    isSectionsConfigLoading,
    isSectionsConfigError,
    showNoSectionsStub,
    showMobileBottomNav,
  };
};
